import { HttpErrorResponse } from '@angular/common/http';

import type { User } from '../models/user.model';
import { PLAYERS_COPY } from './players.copy';
import type { PlayerCard, PlayRequestContact } from './players.model';

/** Grafía de `event-format.util.ts` (no exportada allí). */
const MONTH_NAMES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
] as const;

/**
 * Iniciales del avatar a partir del alias (B6: nunca el nombre real). Se parte por `_`
 * o `-` sin trozos vacíos: primera letra de los dos primeros trozos; con un solo trozo,
 * sus dos primeras letras. En mayúsculas.
 */
export function aliasInitials(alias: string): string {
  const parts = alias.split(/[_-]+/).filter((part) => part !== '');
  if (parts.length === 0) return '';
  const initials = parts.length === 1 ? parts[0].slice(0, 2) : parts[0][0] + parts[1][0];
  return initials.toUpperCase();
}

/** «{cantón}, {provincia}» o solo la provincia. */
export function playerLocationLabel(card: Pick<PlayerCard, 'canton' | 'province'>): string {
  const canton = card.canton?.trim();
  return canton ? `${canton}, ${card.province.name}` : card.province.name;
}

/**
 * «En el directorio desde el 20 de septiembre de 2026» a partir de `listed_since`
 * (`Y-m-d`, ya en hora de Costa Rica). Se parte la cadena y **no** se construye un
 * `Date`: `new Date('2026-09-20')` es medianoche UTC y en Costa Rica (−6) cae el 19.
 * Una cadena que no es `Y-m-d` válida da `''` (no se pinta la línea).
 */
export function formatListedSince(listedSince: string | null | undefined): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(listedSince ?? '');
  if (!match) return '';
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return '';
  return PLAYERS_COPY.card.listedSince(day, MONTH_NAMES[month - 1], match[1]);
}

/**
 * ¿Cuenta verificada? (B30: la API compara `verified === true`, no `email_verified_at`).
 * Lee el `User.verified` de W0a (D16) con `=== true` estricto.
 *
 * Para esta barrera `undefined` cuenta como **no verificado**: el directorio no se pide y
 * «Busco jugadores» no se activa. El docblock de `User.verified` dice que «`undefined` no
 * es sin verificar», pero eso vale para los avisos de B10, no para una barrera de
 * privacidad (B30): ante la duda, no se pide.
 */
export function isVerifiedAccount(user: User | null | undefined): boolean {
  return user?.verified === true;
}

/**
 * Texto que manda el servidor en `{"error": "<copy>", "code": N}` (403, 429 de negocio);
 * `null` si el cuerpo no trae una cadena con contenido (así se usa el fallback local).
 */
export function serverMessage(err: unknown): string | null {
  if (!(err instanceof HttpErrorResponse)) return null;
  const message = (err.error as { error?: unknown } | null)?.error;
  return typeof message === 'string' && message.trim() !== '' ? message : null;
}

/** Zona de la tienda: las fechas de las solicitudes se pintan en hora de Costa Rica. */
const STORE_TIMEZONE = 'America/Costa_Rica';

/**
 * «1 de octubre de 2026» a partir de un ISO 8601 (`created_at` / `responded_at`), en hora
 * de Costa Rica sea cual sea la zona del navegador o del servidor. `Intl` solo da las
 * piezas numéricas; el mes sale de `MONTH_NAMES`, con la misma grafía que el directorio.
 * Una fecha ilegible da `''`.
 */
export function formatRequestDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: STORE_TIMEZONE,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === type)?.value);
  const month = part('month');
  return `${part('day')} de ${MONTH_NAMES[month - 1]} de ${part('year')}`;
}

/** Cómo se pinta un contacto compartido (D33): enlace solo si el valor pasa la validación. */
export type PlayRequestContactView =
  | { kind: 'link'; href: string; text: string }
  | { kind: 'text'; text: string };

const WHATSAPP_PATTERN = /^\+?\d{8,15}$/;
const EMAIL_PATTERN = /^[^\s@<>"'()]+@[^\s@<>"'()]+\.[^\s@<>"'().]+$/;

/**
 * Nunca se monta un `href` con un valor sin validar (D33, MK3): WhatsApp solo con
 * `^\+?\d{8,15}$` (→ `https://wa.me/<dígitos>`), correo solo con un patrón simple
 * (→ `mailto:`); si no casan, texto. Discord, siempre texto. «En la tienda», el texto de
 * §7.2. Un valor vacío para un método que lo necesita da `null` (no se pinta).
 */
export function playRequestContactView(contact: PlayRequestContact): PlayRequestContactView | null {
  if (contact.method === 'tienda') return { kind: 'text', text: PLAYERS_COPY.store.contactText };
  const value = contact.value?.trim() ?? '';
  if (value === '') return null;
  if (contact.method === 'whatsapp' && WHATSAPP_PATTERN.test(value)) {
    return { kind: 'link', href: `https://wa.me/${value.replace(/^\+/, '')}`, text: value };
  }
  if (contact.method === 'email' && EMAIL_PATTERN.test(value)) {
    return { kind: 'link', href: `mailto:${value}`, text: value };
  }
  return { kind: 'text', text: value };
}
